import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import SectionHeading from "@/components/SectionHeading";
import { buildFaqJsonLd } from "@/components/GeoDefinition";
import DevSubmissionForm from "@/components/DevSubmissionForm";

const CRITERIA = [
  "LBE-Fit",
  "Zielgruppenfit",
  "Operator-Fit",
  "Sessiondauer",
  "Einweisbarkeit",
  "Gruppenlogik",
  "Sicherheit",
  "Technische Umsetzbarkeit",
  "Business-Potenzial",
  "Wiederholbarkeit",
];

const STEPS = [
  { n: "01", t: "Konzept einreichen", text: "Entwickler oder Studio reicht Spiel, Prototyp, Pitch oder Konzept ein." },
  { n: "02", t: "LBE- und Business-Fit prüfen", text: "PINE bewertet, ob der Inhalt grundsätzlich zu Standortbetrieb, Zielgruppen und Systemlogik passt." },
  { n: "03", t: "Interner Test", text: "Bei ausreichendem Fit wird das Spiel intern geprüft." },
  { n: "04", t: "Markttest in eigener PVRA", text: "Wenn der interne Test überzeugt, kann ein realer Standorttest mit Gästen erfolgen." },
  { n: "05", t: "Systemaufnahme", text: "Bei positiver Resonanz kann das Spiel Teil des PINE Systems Portfolios werden." },
  { n: "06", t: "Vergütung pro Nutzung", text: "Games, die Teil des Systems werden, werden nutzungsbasiert vergütet. Wenn ein Spiel gebucht und gespielt wird, entsteht Vergütung über Spielminuten beziehungsweise Nutzung." },
];

const DEV_FAQ = [
  {
    q: "Kann ich ein Spiel oder Konzept bei PINE einreichen?",
    a: "Ja. Über das PINE Dev Programm können Entwickler, Studios und kreative Teams Konzepte, Prototypen oder Spiele einreichen. PINE prüft, ob der Inhalt grundsätzlich zu Location-Based Entertainment, Betreiberlogik, Zielgruppen und Systemfähigkeit passt.",
  },
  {
    q: "Garantiert eine Einreichung die Aufnahme ins PINE Systems Portfolio?",
    a: "Nein. Eine Einreichung ist keine Zusage für Test, Aufnahme oder Vergütung. PINE prüft zunächst LBE-Fit, Operator-Fit, Zielgruppenlogik, technische Umsetzbarkeit und mögliches Business-Potenzial.",
  },
  {
    q: "Wie funktioniert die Vergütung?",
    a: "Spiele, die Teil des PINE Systems Portfolios werden, werden nutzungsbasiert vergütet — über Spielminuten beziehungsweise tatsächliche Nutzung an Standorten.",
  },
];

export default function DevProgramm() {
  return (
    <>
      <SEO
        title="PINE Dev Programm – Games für Location-Based Entertainment einreichen"
        description="Das PINE Dev Programm prüft Games, Prototypen und Konzepte auf LBE-Fit, Operator-Fit, Markttauglichkeit und mögliches Systempotenzial."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "PINE Dev Programm",
            url: "https://www.pinexs.de/dev-programm",
            inLanguage: "de-DE",
          },
          buildFaqJsonLd(DEV_FAQ),
        ]}
      />

      {/* HERO */}
      <section className="relative border-b border-[rgba(255,255,255,0.08)] overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-24">
          <div className="overline" data-testid="dev-eyebrow">PINE Dev Programm</div>
          <h1
            className="font-display text-4xl sm:text-5xl md:text-6xl uppercase mt-4 max-w-5xl leading-[1.04]"
            data-testid="dev-headline"
          >
            Aus guten Games werden <span className="text-[#18E096]">marktfähige LBE-Erlebnisse.</span>
          </h1>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            PINE sucht Entwickler, Studios und kreative Teams, die Inhalte nicht nur für private
            Spieler bauen, sondern für echte Standorte, Betreiber und Gästegruppen denken.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#dev-form" className="btn-primary" data-testid="dev-cta-hero">
              Game oder Konzept einreichen <ArrowUpRight size={16} strokeWidth={1.5} />
            </a>
            <Link to="/cooperations" className="btn-secondary">
              Zurück zu Cooperations
            </Link>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="01 · Ausgangslage"
            title="Ein gutes Spiel ist noch kein gutes Standortprodukt."
            subtitle="Viele Entwickler bauen spannende Spiele. Aber im Location-Based-Entertainment-Kontext gelten andere Fragen: Verstehen Gäste das Erlebnis schnell? Kann ein Operator es sicher einweisen? Funktioniert es in Gruppen? Passt die Sessiondauer? Entsteht Wiederholbarkeit? Kann es wirtschaftlich in einem Standort betrieben werden?"
          />
          <p className="text-[#AEB7B7] mt-8 max-w-3xl text-base leading-relaxed">
            PINE Dev Programm schließt die Lücke zwischen kreativer Entwicklung und marktfähigem
            Standortbetrieb.
          </p>
        </div>
      </section>

      {/* CRITERIA */}
      <section className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading overline="02 · Bewertungskriterien" title="Was wir prüfen." />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {CRITERIA.map((c, i) => (
              <div
                key={c}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-6 bg-[#0B0F10] hover:bg-[#141819] transition-colors"
                data-testid={`dev-criteria-${i}`}
              >
                <div className="font-display text-2xl text-[#18E096]">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-base uppercase mt-3 text-white leading-tight">{c}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading overline="03 · Ablauf" title="Vom Konzept zum Markttest." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 bg-[#0B0F10]"
                data-testid={`dev-step-${s.n}`}
              >
                <div className="font-display text-4xl text-[#18E096]">{s.n}</div>
                <h3 className="font-display text-lg md:text-xl uppercase mt-4 text-white leading-tight">{s.t}</h3>
                <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="text-[#AEB7B7] text-sm mt-10 max-w-3xl leading-relaxed">
            Wichtig: Keine Garantie auf Aufnahme. Keine Garantie auf Umsatz. Keine Garantie auf
            Markttest. Jeder Schritt ist ein Prüfpfad.
          </p>
        </div>
      </section>

      {/* FORM */}
      <section id="dev-form" className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="overline mb-4">Einreichung</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] max-w-3xl">
            Game oder Konzept <span className="text-[#18E096]">einreichen.</span>
          </h2>
          <p className="text-[#AEB7B7] mt-6 mb-8 max-w-3xl text-base leading-relaxed">
            Je präziser Sie Spielname, Entwicklungsstand, Multiplayer-Logik, Sessiondauer und
            Zielgruppe beschreiben, desto besser können wir den ersten LBE-Fit einschätzen.
          </p>
          <DevSubmissionForm />
        </div>
      </section>

      {/* SWITCH BACK */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-3">Hinweis</div>
            <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight">
              Sie suchen stattdessen einen Standort?
            </h3>
            <p className="text-[#AEB7B7] mt-4 max-w-2xl">
              Wenn Sie operativ einen Standort planen, beginnt der Weg über die Standortprüfung
              oder den Gründerpfad.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end flex flex-wrap gap-3">
            <Link to="/standort-pruefen" className="btn-secondary" data-testid="dev-switch-standort">
              Standort prüfen <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
